<template>
  <div id="app">
    <header class="site-header">
      <div class="announcement-bar">
        <div class="container announcement-content">
          <span>Cross-Border Direct Sourcing · Best Sellers · Dedicated Consultant Real-Time Follow-up</span>
          <router-link to="/contact" class="announcement-link">
            Book Product Consultant
          </router-link>
        </div>
      </div>

      <nav class="main-nav" :class="{ 'is-scrolled': isScrolled, 'is-open': mobileNavOpen }">
        <div class="container nav-content">
          <router-link v-if="!logoImageError" to="/" class="logo" @click="closeMobileNav">
            <img :src="logoPath" alt="Technology" @error="handleLogoError" />
          </router-link>

          <div class="nav-links">
            <router-link to="/" @click="closeMobileNav">Home</router-link>
            <!-- <button type="button" @click="navigateToSection('new-arrivals')">New Arrivals</button>
            <button type="button" @click="navigateToSection('best-selling')">Best Sellers</button> -->
            <div class="nav-dropdown">
              <router-link to="/products" @click="closeMobileNav" class="nav-link-with-dropdown">Products</router-link>
              <div class="dropdown-menu" v-if="productTypes.length > 0">
                <router-link
                  v-for="type in productTypesForNav"
                  :key="type"
                  :to="{ path: '/products', query: { category: type } }"
                  class="dropdown-item"
                  @click="closeMobileNav"
                >
                  {{ type }}
                </router-link>
              </div>
            </div>
            <router-link to="/wishlist" @click="closeMobileNav">Wish</router-link>
            <router-link to="/orders" @click="closeMobileNav">Orders</router-link>
            <router-link to="/returns" @click="closeMobileNav">Returns</router-link>
            <router-link to="/about" @click="closeMobileNav">About Us</router-link>
            <router-link to="/contact" @click="closeMobileNav">Contact Us</router-link>
            <!-- <router-link to="/payment" @click="closeMobileNav">Payment Guide</router-link> -->
          </div>

          <div class="nav-actions">
            <div class="nav-account" v-if="!isLoggedIn">
              <router-link to="/login" class="account-link" @click="closeMobileNav">
                <span>Sign in</span>
              </router-link>
              <span class="divider">or</span>
              <router-link to="/register" class="account-link" @click="closeMobileNav">
                <span>Register</span>
              </router-link>
            </div>
            <div class="nav-user" v-else>
              <el-dropdown trigger="click" @command="handleUserCommand">
                <span class="user-dropdown">
                  <span class="user-icon">👤</span>
                  <span class="user-email">{{ userEmail }}</span>
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <!-- <el-dropdown-item command="wishlist">Wish List</el-dropdown-item> -->
                    <el-dropdown-item command="edit-account">Edit Account</el-dropdown-item>
                    <el-dropdown-item command="change-password">Change Password</el-dropdown-item>
                    <el-dropdown-item divided command="logout">Logout</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
            <router-link to="/cart" class="nav-cart" @click="closeMobileNav">
              <span class="cart-icon">🛒</span>
              <div class="cart-info">
                <span class="label">Cart</span>
                <span class="meta">{{ cartSummary.items }} items · ${{ cartSummary.total }}</span>
              </div>
            </router-link>
          </div>

          <button class="mobile-toggle" type="button" @click="mobileNavOpen = !mobileNavOpen">
            <span v-if="!mobileNavOpen">Menu</span>
            <span v-else>Close</span>
          </button>
        </div>
        <div class="mobile-panel" v-if="mobileNavOpen">
          <div class="mobile-links">
            <router-link to="/" @click="closeMobileNav">Home</router-link>
            <!-- <button type="button" @click="navigateToSection('new-arrivals')">New Arrivals</button>
            <button type="button" @click="navigateToSection('best-selling')">Best Sellers</button> -->
            <router-link to="/products" @click="closeMobileNav">Products</router-link>
            <router-link to="/wishlist" @click="closeMobileNav">Wish</router-link>
            <router-link to="/orders" @click="closeMobileNav">Orders</router-link>
            <router-link to="/returns" @click="closeMobileNav">Returns</router-link>
            <router-link to="/about" @click="closeMobileNav">About Us</router-link>
            <router-link to="/contact" @click="closeMobileNav">Contact Us</router-link>
            <!-- <router-link to="/payment" @click="closeMobileNav">Payment Guide</router-link> -->
            <router-link to="/cart" @click="closeMobileNav">Cart</router-link>
          </div>
          <div class="mobile-actions">
            <div class="mobile-account" v-if="!isLoggedIn">
              <router-link to="/login" class="mobile-account-link" @click="closeMobileNav">Sign in</router-link>
              <router-link to="/register" class="mobile-account-link register" @click="closeMobileNav">Register</router-link>
            </div>
            <div class="mobile-user" v-else>
              <div class="mobile-user-header">
                <span class="user-info">👤 {{ userEmail }}</span>
              </div>
              <div class="mobile-user-menu">
                <!-- <router-link to="/orders" class="mobile-menu-item" @click="closeMobileNav">My Orders</router-link> -->
                <!-- <router-link to="/wishlist" class="mobile-menu-item" @click="closeMobileNav">Wish List</router-link> -->
                <router-link to="/account/edit" class="mobile-menu-item" @click="closeMobileNav">Edit Account</router-link>
                <router-link to="/forgot-password" class="mobile-menu-item" @click="closeMobileNav">Change Password</router-link>
                <button type="button" class="mobile-menu-item logout" @click="handleLogout">Logout</button>
              </div>
            </div>
            <router-link to="/cart" class="mobile-cart" @click="closeMobileNav">
              <span>Cart</span>
              <span>{{ cartSummary.items }} items · ${{ cartSummary.total }}</span>
            </router-link>
          </div>
        </div>
      </nav>
    </header>

    <!-- Back to top button -->
    <transition name="fade">
      <button
        v-if="showBackToTop"
        class="back-to-top"
        type="button"
        @click="scrollToTop"
        aria-label="Back to top"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
      </button>
    </transition>

    <main class="app-main">
      <router-view />
    </main>

    <footer class="site-footer">
      <div class="container newsletter-section">
        <h3>Newsletter</h3>
        <p class="newsletter-text">Subscribe to get special offers, free giveaways, and updates.</p>
        <form class="footer-subscribe" @submit.prevent="handleSubscribe">
          <input type="email" v-model="subscribeEmail" placeholder="Enter your email" required />
          <button type="submit"><i class="fas fa-paper-plane"></i></button>
        </form>
        <!-- <div class="footer-social">
          <a href="#" aria-label="Facebook" title="Facebook"><i class="fab fa-facebook-f"></i></a>
          <a href="#" aria-label="Twitter" title="Twitter"><i class="fab fa-twitter"></i></a>
          <a href="#" aria-label="Instagram" title="Instagram"><i class="fab fa-instagram"></i></a>
          <a href="#" aria-label="LinkedIn" title="LinkedIn"><i class="fab fa-linkedin-in"></i></a>
          <a href="#" aria-label="YouTube" title="YouTube"><i class="fab fa-youtube"></i></a>
        </div> -->
      </div>

      <div class="container footer-grid">
        <div class="footer-brand">
          <h3>Contact Us</h3>
          <div class="footer-contact">
            <div class="contact-item">
              <i class="fas fa-envelope"></i>
              <a :href="`mailto:${companyInfo.email}`">{{ companyInfo.email || 'support@example.com' }}</a>
            </div>
            <div class="contact-item">
              <i class="fas fa-phone"></i>
              <span>{{ companyInfo.phone || '+1 (800) 123-4567' }}</span>
            </div>
            <div class="contact-item">
              <i class="fas fa-map-marker-alt"></i>
              <span>{{ companyInfo.address || '123 Business St, Suite 100, City, State 12345' }}</span>
            </div>
          </div>
        </div>

        <div class="footer-column">
          <h3>Shop</h3>
          <router-link to="/">Featured Products</router-link>
          <router-link to="/products">All Products</router-link>
          <!-- <button type="button" @click="navigateToSection('new-arrivals')">New Arrivals</button>
          <button type="button" @click="navigateToSection('best-selling')">Best Sellers</button>
          <button type="button" @click="navigateToSection('collections')">Collections</button> -->
          <router-link to="/wishlist">Wish List</router-link>
          <router-link to="/cart">Shopping Cart</router-link>
        </div>

        <div class="footer-column">
          <h3>Customer Service</h3>
          <router-link to="/orders">My Orders</router-link>
          <router-link to="/returns">Returns & Refunds</router-link>
          <router-link to="/account/edit">My Account</router-link>
          <router-link to="/payment">Payment & After-Sales</router-link>
          <!-- <router-link to="/contact">FAQ</router-link> -->
        </div>

        <div class="footer-column">
          <h3>Information</h3>
          <router-link to="/about">About Us</router-link>
          <router-link to="/contact">Contact Us</router-link>
          <router-link to="/privacy">Privacy Policy</router-link>
          <router-link to="/terms">Terms & Conditions</router-link>
        </div>
      </div>
      <div class="footer-bottom">
        <div class="container footer-bottom-content">
          <p>Copyright &copy; 2025 {{ currentDomain }}. All Rights Reserved.</p>
          <div class="footer-payment">
            <span>We Accept:</span>
            <i class="fab fa-cc-visa"></i>
            <i class="fab fa-cc-mastercard"></i>
            <i class="fab fa-cc-amex"></i>
            <i class="fab fa-cc-paypal"></i>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { error as logError } from '@/utils/logger'

export default {
  name: 'App',
  data() {
    return {
      companyInfo: {
        address: '',
        phone: '',
        email: ''
      },
      mobileNavOpen: false,
      isScrolled: false,
      showBackToTop: false,
      cartItems: [], // Shopping cart items list
      wishlistItems: [], // Wishlist items list
      currentUser: null, // Current logged in user
      subscribeEmail: '', // Newsletter subscription email
      productTypes: [], // Product categories for dropdown
      logoImageError: false // Track if logo image failed to load
    }
  },
  computed: {
    currentDomain() {
      const domain = window.location.hostname.split('.')[1]
      return domain.charAt(0).toUpperCase() + domain.slice(1)
    },
    logoPath() {
      const domain = window.location.hostname.split('.')[1]
      return `/${domain}.png`
    },
    cartSummary() {
      const items = this.cartItems.reduce((sum, item) => sum + item.quantity, 0)
      const total = this.cartItems.reduce((sum, item) => {
        const price = parseFloat(item.prodPrice) || 0
        return sum + price * item.quantity
      }, 0)
      return {
        items,
        total: total.toFixed(2)
      }
    },
    isLoggedIn() {
      return !!this.currentUser
    },
    userEmail() {
      return this.currentUser?.emailAddress || ''
    },
    wishlistCount() {
      return this.wishlistItems.length
    },
    productTypesForNav() {
      // Navigation dropdown shows only first 10 types
      return this.productTypes.slice(0, 10)
    }
  },
  provide() {
    return {
      addToCart: this.addToCart,
      removeFromCart: this.removeFromCart,
      updateCartQuantity: this.updateCartQuantity,
      getCartItems: () => this.cartItems,
      clearCart: this.clearCart,
      addToWishlist: this.addToWishlist,
      removeFromWishlist: this.removeFromWishlist,
      getWishlistItems: () => this.wishlistItems,
      isInWishlist: this.isInWishlist,
      clearWishlist: this.clearWishlist,
      getCompanyInfo: () => this.companyInfo,
      getProductTypes: () => this.productTypes
    }
  },
  mounted() {
    this.fetchCompanyInfo()
    this.fetchProductTypes()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    // Load cart data from localStorage
    this.loadCartFromStorage()
    // Load user data from localStorage
    this.loadUserFromStorage()
    // Load wishlist data from localStorage
    this.loadWishlistFromStorage()
    // Listen for user login event
    window.addEventListener('user-login', this.handleUserLogin)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('user-login', this.handleUserLogin)
  },
  watch: {
    $route() {
      this.mobileNavOpen = false
    },
    logoPath() {
      // Reset error state when logo path changes
      this.logoImageError = false
    }
  },
  methods: {
    handleLogoError() {
      this.logoImageError = true
    },
    fetchCompanyInfo() {
      const params = {
        source: window.location.hostname.split('.')[1]
      }
      this.$axios.post('/asin/company', params).then(response => {
        if (response.data.code === 0) {
          this.companyInfo = response.data.data
        } else {
          throw new Error(response.data.message || 'Failed to fetch company information')
        }
      }).catch(error => {
        logError('Error fetching company information:', error)
      })
    },
      fetchProductTypes() {
        const params = {
          prodDomain: window.location.hostname.split('.')[1]
        }
        this.$axios.post('/asin/searchProdType', params).then(response => {
          if (response.data.code === 0 && response.data.data && Array.isArray(response.data.data)) {
            // Extract prodType from each object and limit to maximum 20 types (sorted by count)
            this.productTypes = response.data.data
              .map(item => item.prodType)
              .filter(type => type && type.trim())
              .slice(0, 20)
          }
        }).catch(error => {
          logError('Error fetching product types:', error)
        })
      },
    handleScroll() {
      this.isScrolled = window.scrollY > 20
      this.showBackToTop = window.scrollY > 300
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    closeMobileNav() {
      this.mobileNavOpen = false
    },
    navigateToSection(sectionId) {
      const executeScroll = () => {
        this.$nextTick(() => {
          const anchor = document.getElementById(sectionId)
          if (anchor) {
            anchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }

      if (this.$route.path !== '/') {
        this.$router.push({ path: '/', hash: `#${sectionId}` }).then(() => {
          executeScroll()
        })
      } else {
        executeScroll()
      }
      this.mobileNavOpen = false
    },
    // Add to cart
    addToCart(product) {
      if (!product || !product.prodId) return

      const existingItem = this.cartItems.find(item => item.prodId === product.prodId)

      if (existingItem) {
        existingItem.quantity += 1
      } else {
        this.cartItems.push({
          ...product,
          quantity: 1
        })
      }

      this.saveCartToStorage()
    },
    // Remove from cart
    removeFromCart(prodId) {
      const index = this.cartItems.findIndex(item => item.prodId === prodId)
      if (index > -1) {
        this.cartItems.splice(index, 1)
        this.saveCartToStorage()
      }
    },
    // Update cart item quantity
    updateCartQuantity(prodId, quantity) {
      const item = this.cartItems.find(item => item.prodId === prodId)
      if (item) {
        if (quantity <= 0) {
          this.removeFromCart(prodId)
        } else {
          item.quantity = quantity
          this.saveCartToStorage()
        }
      }
    },
    // Clear cart
    clearCart() {
      this.cartItems = []
      this.saveCartToStorage()
    },
    // Save cart to localStorage
    saveCartToStorage() {
      try {
        localStorage.setItem('cartItems', JSON.stringify(this.cartItems))
      } catch (error) {
        logError('Failed to save cart to storage:', error)
      }
    },
    // Load cart from localStorage
    loadCartFromStorage() {
      try {
        const saved = localStorage.getItem('cartItems')
        if (saved) {
          this.cartItems = JSON.parse(saved)
        }
      } catch (error) {
        logError('Failed to load cart from storage:', error)
      }
    },
    // Load user from localStorage
    loadUserFromStorage() {
      try {
        const saved = localStorage.getItem('user')
        if (saved) {
          this.currentUser = JSON.parse(saved)
        }
      } catch (error) {
        logError('Failed to load user from storage:', error)
      }
    },
    // Add to wishlist
    addToWishlist(product) {
      if (!product || !product.prodId) return

      // Check if user is logged in
      if (!this.isLoggedIn) {
        this.$router.push('/login')
        return false
      }

      // Check if already in wishlist
      const existingItem = this.wishlistItems.find(item => item.prodId === product.prodId)
      if (existingItem) {
        return false // Already in wishlist
      }

      this.wishlistItems.push({
        ...product,
        addedAt: new Date().toISOString()
      })
      this.saveWishlistToStorage()
      return true
    },
    // Remove from wishlist
    removeFromWishlist(prodId) {
      const index = this.wishlistItems.findIndex(item => item.prodId === prodId)
      if (index > -1) {
        this.wishlistItems.splice(index, 1)
        this.saveWishlistToStorage()
      }
    },
    // Check if product is in wishlist
    isInWishlist(prodId) {
      return this.wishlistItems.some(item => item.prodId === prodId)
    },
    // Clear wishlist
    clearWishlist() {
      this.wishlistItems = []
      this.saveWishlistToStorage()
    },
    // Save wishlist to localStorage (user-specific)
    saveWishlistToStorage() {
      if (!this.currentUser) return

      try {
        const userId = this.currentUser.emailAddress || this.currentUser.id
        const key = `wishlist_${userId}`
        localStorage.setItem(key, JSON.stringify(this.wishlistItems))
      } catch (error) {
        logError('Failed to save wishlist to storage:', error)
      }
    },
    // Load wishlist from localStorage (user-specific)
    loadWishlistFromStorage() {
      if (!this.currentUser) {
        this.wishlistItems = []
        return
      }

      try {
        const userId = this.currentUser.emailAddress || this.currentUser.id
        const key = `wishlist_${userId}`
        const saved = localStorage.getItem(key)
        if (saved) {
          this.wishlistItems = JSON.parse(saved)
        } else {
          this.wishlistItems = []
        }
      } catch (error) {
        logError('Failed to load wishlist from storage:', error)
        this.wishlistItems = []
      }
    },
    // Handle user dropdown commands
    handleUserCommand(command) {
      if (command === 'logout') {
        this.handleLogout()
      } else if (command === 'edit-account') {
        this.$router.push('/account/edit')
      } else if (command === 'orders') {
        this.$router.push('/orders')
      } else if (command === 'wishlist') {
        this.$router.push('/wishlist')
      } else if (command === 'change-password') {
        this.$router.push('/forgot-password')
      }
    },
    // Handle logout
    handleLogout() {
      const emailAddress = this.currentUser?.emailAddress
      if (emailAddress) {
        this.$axios.post('/userDomain/logout', { emailAddress }).catch(error => {
          logError('Logout error:', error)
        })
      }
      this.currentUser = null
      this.wishlistItems = [] // Clear wishlist on logout
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      this.closeMobileNav()
      this.$router.push('/login')
    },
    // Handle user login event
    handleUserLogin() {
      this.loadUserFromStorage()
      this.loadWishlistFromStorage() // Load wishlist when user logs in
    },
    // Handle newsletter subscription
    handleSubscribe() {
      if (!this.subscribeEmail) {
        return
      }
      // Show success message
      ElMessage({
        message: 'Successfully subscribed! You will receive our latest updates.',
        type: 'success',
        duration: 3000
      })
      // Clear input
      this.subscribeEmail = ''
    }
  }
}
</script>

<style>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-light);
}

.app-main {
  flex: 1;
  margin-top: 0;
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 1000;
}

.announcement-bar {
  background: var(--brand-primary);
  color: #fff;
  font-size: 0.95rem;
}

.announcement-content {
  padding: 10px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.announcement-link {
  color: #fff;
  font-weight: 600;
  text-decoration: underline;
  transition: var(--transition-base);
  white-space: nowrap;
}

.announcement-link:hover {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
}

.main-nav {
  background: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  transition: var(--transition-base);
}

.main-nav.is-scrolled {
  box-shadow: var(--shadow-sm);
}

.nav-content {
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
}

.logo img {
  height: 48px;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 18px;
}

.nav-links > a,
.nav-links > button {
  border: none;
  background: transparent;
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--text-primary);
  padding: 10px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: var(--transition-base);
}

.nav-links > a:hover,
.nav-links > button:hover,
.nav-links > a.router-link-active {
  color: var(--brand-accent);
  background: rgba(0, 113, 227, 0.08);
}

/* Product dropdown menu */
.nav-dropdown {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.nav-dropdown::before {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  height: 20px;
  background: transparent;
}

.nav-link-with-dropdown {
  border: none;
  background: transparent;
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--text-primary);
  padding: 10px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: var(--transition-base);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.nav-link-with-dropdown:hover,
.nav-link-with-dropdown.router-link-active {
  color: var(--brand-accent);
  background: rgba(0, 113, 227, 0.08);
}

.nav-link-with-dropdown::after {
  content: '▼';
  font-size: 0.7rem;
  opacity: 0.6;
  transition: transform 0.3s ease;
}

.nav-dropdown:hover .nav-link-with-dropdown::after {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  background: white;
  min-width: 200px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-radius: var(--radius-md);
  padding: 8px 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  z-index: 1000;
  border: 1px solid rgba(15, 23, 42, 0.08);
  pointer-events: none;
}

.nav-dropdown:hover .dropdown-menu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  pointer-events: auto;
}

.dropdown-item {
  display: block;
  padding: 10px 20px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.dropdown-item:hover {
  background: rgba(0, 113, 227, 0.08);
  color: var(--brand-accent);
  padding-left: 24px;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 20px;
}

.nav-account {
  display: flex;
  align-items: center;
  gap: 8px;
}

.account-link {
  font-weight: 600;
  color: var(--text-primary);
  padding: 8px 12px;
  border-radius: 8px;
  transition: var(--transition-base);
}

.account-link:hover {
  color: var(--brand-accent);
  background: rgba(0, 113, 227, 0.08);
}

.nav-account .divider {
  color: var(--text-muted);
  font-size: 0.9rem;
}

.nav-user {
  display: flex;
  align-items: center;
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid rgba(0, 113, 227, 0.12);
  cursor: pointer;
  transition: var(--transition-base);
}

.user-dropdown:hover {
  background: linear-gradient(135deg, #e3f2ff 0%, #dbeafe 100%);
  border-color: rgba(0, 113, 227, 0.2);
}

.user-icon {
  font-size: 1.1rem;
}

.user-email {
  font-weight: 600;
  color: var(--brand-primary);
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-cart {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid rgba(0, 113, 227, 0.12);
  color: var(--brand-primary);
  font-weight: 600;
  transition: var(--transition-base);
  text-decoration: none;
  position: relative;
}

.nav-cart:hover {
  background: linear-gradient(135deg, #e3f2ff 0%, #dbeafe 100%);
  border-color: rgba(0, 113, 227, 0.2);
  box-shadow: 0 4px 12px rgba(0, 113, 227, 0.15);
  transform: translateY(-2px);
}

.cart-icon {
  font-size: 1.4rem;
  line-height: 1;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.cart-info {
  display: flex;
  flex-direction: column;
  line-height: 1.4;
  gap: 2px;
}

.cart-info .label {
  font-weight: 700;
  color: var(--brand-primary);
  font-size: 0.95rem;
  letter-spacing: 0.01em;
}

.cart-info .meta {
  color: #64748b;
  font-weight: 600;
  font-size: 0.85rem;
}

.contact-btn {
  padding: 12px 24px;
  font-size: 0.95rem;
  white-space: nowrap;
  background: linear-gradient(135deg, #0071e3 0%, #005bb5 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(0, 113, 227, 0.2);
}

.contact-btn:hover {
  background: linear-gradient(135deg, #005bb5 0%, #004a94 100%);
  box-shadow: 0 4px 12px rgba(0, 113, 227, 0.3);
  transform: translateY(-1px);
}

.mobile-toggle {
  display: none;
  border: none;
  background: rgba(15, 23, 42, 0.08);
  padding: 8px 16px;
  border-radius: 999px;
  font-weight: 600;
}

.mobile-panel {
  display: none;
}

.site-footer {
  background: var(--brand-primary);
  color: rgba(255, 255, 255, 0.85);
  padding: 0;
  margin-top: 80px;
}

.newsletter-section {
  padding: 48px 0;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.newsletter-section h3 {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 12px;
  color: #fff;
}

.newsletter-section .newsletter-text {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 24px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.newsletter-section .footer-subscribe {
  max-width: 480px;
  margin: 0 auto 28px;
}

.newsletter-section .footer-social {
  justify-content: center;
  margin-top: 0;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.5fr repeat(3, 1fr);
  gap: 48px;
  padding: 48px 0;
  align-items: start;
}

.footer-brand img {
  height: 52px;
  margin-bottom: 18px;
}

.footer-brand h3 {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.footer-desc {
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 24px;
}

.footer-contact {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.contact-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  font-size: 0.9rem;
}

.contact-item i {
  width: 18px;
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--brand-accent);
}

.contact-item a,
.contact-item span {
  color: rgba(255, 255, 255, 0.8);
  transition: color 0.3s ease;
}

.contact-item a:hover {
  color: #fff;
}

.footer-column h3 {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #fff;
}

.footer-column a,
.footer-column button {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
  background: transparent;
  border: none;
  padding: 0;
  font-size: 0.95rem;
  text-align: left;
  cursor: pointer;
}

.footer-column a:hover,
.footer-column button:hover {
  color: #fff;
}

.newsletter-text {
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 16px;
}

.footer-subscribe {
  margin-top: 16px;
  display: flex;
  gap: 8px;
  max-width: 360px;
}

.footer-subscribe input {
  flex: 1;
  height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.footer-subscribe input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.footer-subscribe input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
}

.footer-subscribe button {
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 50%;
  border: none;
  background: var(--brand-accent);
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.footer-subscribe button:hover {
  background: #005bb5;
  transform: scale(1.05);
}

.footer-social {
  margin-top: 20px;
  display: flex;
  gap: 12px;
}

.footer-social a {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  transition: all 0.3s ease;
}

.footer-social a:hover {
  background: #fff;
  color: var(--brand-primary);
  transform: translateY(-3px);
}

.footer-bottom {
  margin-top: 48px;
  padding: 24px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

.footer-payment {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.footer-payment span {
  font-size: 0.85rem;
}

.footer-payment i {
  font-size: 1.8rem;
  color: rgba(255, 255, 255, 0.8);
  transition: color 0.3s ease;
}

.footer-payment i:hover {
  color: #fff;
}

.footer-links {
  display: flex;
  gap: 20px;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.8);
}

.footer-links a:hover {
  color: #fff;
}

@media (max-width: 1024px) {
  .app-main {
    margin-top: 0;
  }

  .nav-links {
    display: none;
  }

  .nav-actions {
    display: none;
  }

  .mobile-toggle {
    display: inline-flex;
  }

  .main-nav.is-open .mobile-panel {
    display: block;
    background: #fff;
    border-top: 1px solid rgba(15, 23, 42, 0.06);
  }

  .mobile-links,
  .mobile-actions {
    display: grid;
    gap: 16px;
    padding: 18px 24px;
  }

  .mobile-links button,
  .mobile-links a {
    text-align: left;
    padding: 12px 0;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    font-weight: 600;
    color: var(--text-primary);
  }

  .mobile-links button:last-child,
  .mobile-links a:last-child {
    border-bottom: none;
  }

  .mobile-actions .cta-button {
    width: 100%;
  }

  .mobile-cart {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: rgba(0, 113, 227, 0.08);
    border-radius: 12px;
    font-weight: 600;
    color: var(--brand-primary);
  }

  .mobile-account {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
  }

  .mobile-account-link {
    flex: 1;
    text-align: center;
    padding: 12px 16px;
    border-radius: 10px;
    font-weight: 600;
    background: rgba(0, 113, 227, 0.08);
    color: var(--brand-primary);
    transition: var(--transition-base);
  }

  .mobile-account-link.register {
    background: linear-gradient(135deg, #0071e3 0%, #0b66c3 100%);
    color: #fff;
  }

  .mobile-user {
    background: rgba(0, 113, 227, 0.08);
    border-radius: 12px;
    margin-bottom: 12px;
    overflow: hidden;
  }

  .mobile-user-header {
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0, 113, 227, 0.1);
  }

  .mobile-user .user-info {
    font-weight: 600;
    color: var(--brand-primary);
    display: block;
  }

  .mobile-user-menu {
    display: flex;
    flex-direction: column;
  }

  .mobile-menu-item {
    padding: 12px 16px;
    color: var(--text-primary);
    font-weight: 500;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(0, 113, 227, 0.1);
    text-align: left;
    cursor: pointer;
    transition: var(--transition-base);
    text-decoration: none;
  }

  .mobile-menu-item:last-child {
    border-bottom: none;
  }

  .mobile-menu-item:hover {
    background: rgba(0, 113, 227, 0.05);
  }

  .mobile-menu-item.logout {
    color: var(--brand-warning);
    font-weight: 600;
  }

  .mobile-menu-item.logout:hover {
    background: rgba(255, 107, 107, 0.08);
  }

  .footer-grid {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 36px;
  }

  .footer-bottom-content {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .announcement-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }


  .app-main {
    margin-top: 0;
  }

  .nav-content {
    padding: 14px 18px;
  }

  .logo img {
    height: 40px;
  }

  .footer-subscribe {
    flex-direction: column;
  }

  .footer-subscribe button {
    width: 100%;
  }

  .footer-links {
    flex-wrap: wrap;
  }
}

/* Back to top button */
.back-to-top {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #dc2626;
  color: #fff;
  border: none;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: all 0.3s ease;
}

.back-to-top:hover {
  background: #b91c1c;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
}

.back-to-top:active {
  transform: translateY(0);
}

.back-to-top svg {
  width: 20px;
  height: 20px;
  display: block;
  margin: 0 auto;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .back-to-top {
    bottom: 24px;
    right: 24px;
    width: 44px;
    height: 44px;
  }

  .back-to-top svg {
    width: 18px;
    height: 18px;
  }
}
</style>
